// ---------------------------------------------------------------------------
// Prezio Onboard Widget — SessionContext
// ---------------------------------------------------------------------------
// Central state manager. Uses useReducer for complex state transitions.
// Two values exposed: SessionState (data) and SessionActions (methods).
// ---------------------------------------------------------------------------

import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { ApiError } from '@/api/types';
import { getLocale, isSupportedLocale, loadLocale, toWidgetLocale } from '@/i18n';
import { notifyParent } from '@/utils/parentBridge';
import type {
  OnboardSession,
  SessionStatus,
  StepId,
  StepOptionsResponse,
  ValidationResult,
} from '@/api/types';
import type { ApiClient } from '@/api/client';
import type { CompletionResult, StepChangeEvent, ErrorEvent } from '@/types/events';
import { DEFAULT_LOCALE, TERMINAL_STATUSES, STEP_ORDER } from '@/utils/constants';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface SessionState {
  session: OnboardSession | null;
  isLoading: boolean;
  isSubmitting: boolean;
  error: ApiError | null;
  optionsCache: Map<StepId, StepOptionsResponse>;
  /** Snapshot of collected_data taken before navigateToStep so downstream
   *  steps can fall back to previously-entered values after the backend
   *  clears data on re-submission. Cleared when the user returns to summary. */
  preEditSnapshot: Record<string, unknown> | null;
  /** The active widget locale. Changing it re-renders every step. */
  locale: string;
}

function createInitialState(): SessionState {
  return {
    session: null,
    isLoading: true,
    isSubmitting: false,
    error: null,
    optionsCache: new Map(),
    preEditSnapshot: null,
    locale: getLocale(),
  };
}

// ---------------------------------------------------------------------------
// Actions (public interface)
// ---------------------------------------------------------------------------

export interface SessionActions {
  fetchSession: () => Promise<void>;
  fetchOptions: (step: StepId) => Promise<StepOptionsResponse>;
  submitStep: (step: StepId, data: Record<string, unknown>) => Promise<OnboardSession>;
  validateStep: (step: StepId, data: Record<string, unknown>) => Promise<ValidationResult>;
  completeSession: () => Promise<OnboardSession>;
  cancelSession: (reason?: string) => Promise<void>;
  goBack: () => Promise<void>;
  navigateToStep: (step: StepId) => Promise<void>;
  /** Load and activate a locale, then re-fetch options in that language. */
  changeLocale: (locale: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: 'FETCH_SESSION_START' }
  | { type: 'FETCH_SESSION_SUCCESS'; session: OnboardSession }
  | { type: 'FETCH_SESSION_ERROR'; error: ApiError }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; session: OnboardSession }
  | { type: 'SUBMIT_ERROR'; error: ApiError }
  | { type: 'CACHE_OPTIONS'; step: StepId; options: StepOptionsResponse }
  | { type: 'INVALIDATE_CACHE_AFTER'; step: StepId }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_ERROR'; error: ApiError }
  | { type: 'SNAPSHOT_DATA'; data: Record<string, unknown> }
  | { type: 'CLEAR_SNAPSHOT' }
  | { type: 'SET_LOCALE'; locale: string };

function reducer(state: SessionState, action: Action): SessionState {
  switch (action.type) {
    case 'FETCH_SESSION_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SESSION_SUCCESS':
      return { ...state, isLoading: false, session: action.session, error: null };

    case 'FETCH_SESSION_ERROR':
      return { ...state, isLoading: false, error: action.error };

    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: null };

    case 'SUBMIT_SUCCESS': {
      // When the user returns to summary after editing, clear the snapshot
      const clearSnapshot = action.session.current_step === 'summary';
      return {
        ...state,
        isSubmitting: false,
        session: action.session,
        error: null,
        ...(clearSnapshot ? { preEditSnapshot: null } : {}),
      };
    }

    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.error };

    case 'CACHE_OPTIONS': {
      const next = new Map(state.optionsCache);
      next.set(action.step, action.options);
      return { ...state, optionsCache: next };
    }

    case 'INVALIDATE_CACHE_AFTER': {
      const idx = STEP_ORDER.indexOf(action.step);
      if (idx === -1) return state;
      const next = new Map(state.optionsCache);
      for (const key of next.keys()) {
        if (STEP_ORDER.indexOf(key) > idx) next.delete(key);
      }
      return { ...state, optionsCache: next };
    }

    case 'CLEAR_ERROR':
      return { ...state, error: null };

    case 'SET_ERROR':
      return { ...state, error: action.error };

    case 'SNAPSHOT_DATA':
      return { ...state, preEditSnapshot: action.data };

    case 'CLEAR_SNAPSHOT':
      return { ...state, preEditSnapshot: null };

    // Cached options hold API labels in the old language.
    case 'SET_LOCALE':
      return { ...state, locale: action.locale, optionsCache: new Map() };
  }
}

// ---------------------------------------------------------------------------
// Contexts
// ---------------------------------------------------------------------------

const StateCtx = createContext<SessionState | null>(null);
const ActionsCtx = createContext<SessionActions | null>(null);

// ---------------------------------------------------------------------------
// Provider props
// ---------------------------------------------------------------------------

interface SessionProviderProps {
  apiClient: ApiClient;
  sessionId: string;
  children: ReactNode;
  onComplete?: (result: CompletionResult) => void;
  onCancel?: () => void;
  onError?: (error: ErrorEvent) => void;
  onStepChange?: (event: StepChangeEvent) => void;
}

// ---------------------------------------------------------------------------
// Helper — check if session is in a terminal state
// ---------------------------------------------------------------------------

function isTerminal(status: SessionStatus): boolean {
  return (TERMINAL_STATUSES as readonly string[]).includes(status);
}

/** Runs `call` after any in-flight language save, so a session action can't write the old language back. */
function afterLocaleSave<T>(
  ref: { readonly current: Promise<void> | null },
  call: () => Promise<T>,
): Promise<T> {
  return ref.current ? ref.current.then(call) : call();
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function SessionProvider({
  apiClient,
  children,
  onComplete,
  onCancel,
  onError,
  onStepChange,
}: SessionProviderProps) {
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState);

  // Keep callbacks in refs so they don't cause re-renders or stale closures
  const onCompleteRef = useRef(onComplete);
  const onCancelRef = useRef(onCancel);
  const onErrorRef = useRef(onError);
  const onStepChangeRef = useRef(onStepChange);

  onCompleteRef.current = onComplete;
  onCancelRef.current = onCancel;
  onErrorRef.current = onError;
  onStepChangeRef.current = onStepChange;

  // Track previous step for direction detection
  const prevStepRef = useRef<StepId | null>(null);

  // Ref for reading state inside stable callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  // Bumped on each language switch, so options requested in the old
  // language are not cached once they arrive.
  const localeVersionRef = useRef(0);

  // The session's saved language is applied on the first load only.
  const sessionLocaleAppliedRef = useRef(false);

  // The backend saves the whole session on step actions, which would write
  // back the old language if a language save were still in flight.
  const localeSaveRef = useRef<Promise<void> | null>(null);

  /** Load and activate a locale; returns false when it was already active. */
  const applyLocale = useCallback(async (locale: string): Promise<boolean> => {
    await loadLocale(locale);
    const active = getLocale();
    if (active === stateRef.current.locale) return false;
    localeVersionRef.current += 1;
    dispatch({ type: 'SET_LOCALE', locale: active });
    return true;
  }, []);

  // ------- Actions -------

  const fetchSession = useCallback(async () => {
    dispatch({ type: 'FETCH_SESSION_START' });
    try {
      const session = await apiClient.getSession();
      if (!sessionLocaleAppliedRef.current) {
        sessionLocaleAppliedRef.current = true;
        const saved = session.locale ? toWidgetLocale(session.locale) : null;
        if (saved) await applyLocale(saved);
      }
      dispatch({ type: 'FETCH_SESSION_SUCCESS', session });

      // If session loaded in a terminal state, fire appropriate callback
      if (isTerminal(session.status)) {
        if (session.status === 'completed' && session.result) {
          const completionPayload = {
            location_id: session.result.location_id,
            tariff_ids: session.result.tariff_ids,
            session_id: session.id,
          };
          onCompleteRef.current?.(completionPayload);
          notifyParent('complete', completionPayload);
        } else if (session.status === 'expired') {
          const expiredPayload = {
            code: 'session_expired',
            message: 'This session has expired',
          };
          onErrorRef.current?.(expiredPayload);
          notifyParent('error', expiredPayload);
        }
      }
    } catch (err) {
      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, { code: 'unknown', detail: 'Failed to fetch session' });
      dispatch({ type: 'FETCH_SESSION_ERROR', error: apiError });

      if (apiError.status === 401) {
        const authPayload = {
          code: 'authentication_failed',
          message: apiError.message,
        };
        onErrorRef.current?.(authPayload);
        notifyParent('error', authPayload);
      }
    }
  }, [apiClient, applyLocale]);

  const fetchOptions = useCallback(
    async (step: StepId): Promise<StepOptionsResponse> => {
      // Return from cache if available
      const cached = stateRef.current.optionsCache.get(step);
      if (cached) return cached;

      // Prefer the absolute options_url from the session when available,
      // but ONLY when fetching the step described by next_step (the URL is
      // specific to that step). For any other step (e.g. prefetch) we must
      // construct the URL from the step ID.
      const nextStep = stateRef.current.session?.next_step;
      const absUrl =
        nextStep?.step_id === step && nextStep.options_url ? nextStep.options_url : undefined;

      const localeVersion = localeVersionRef.current;
      // The session language wins over Accept-Language: fetch once a switch is saved.
      const options = await afterLocaleSave(localeSaveRef, () =>
        absUrl ? apiClient.getOptions(absUrl) : apiClient.getStepOptions(step),
      );

      if (localeVersion === localeVersionRef.current) {
        dispatch({ type: 'CACHE_OPTIONS', step, options });
      }
      return options;
    },
    [apiClient],
  );

  const submitStep = useCallback(
    async (step: StepId, data: Record<string, unknown>): Promise<OnboardSession> => {
      dispatch({ type: 'SUBMIT_START' });
      try {
        const session = await afterLocaleSave(localeSaveRef, () =>
          apiClient.submitStep(step, data),
        );
        dispatch({ type: 'SUBMIT_SUCCESS', session });

        // Fire onStepChange if step actually changed
        if (session.current_step && session.current_step !== step) {
          const prevIndex = prevStepRef.current ? getStepIndex(prevStepRef.current) : -1;
          const nextIndex = getStepIndex(session.current_step);
          const direction = nextIndex > prevIndex ? 'forward' : 'backward';

          prevStepRef.current = session.current_step;
          onStepChangeRef.current?.({
            stepId: session.current_step,
            progress: session.progress.percentage,
            direction,
          });
        }

        return session;
      } catch (err) {
        const apiError =
          err instanceof ApiError
            ? err
            : new ApiError(0, { code: 'unknown', detail: 'Step submission failed' });
        dispatch({ type: 'SUBMIT_ERROR', error: apiError });

        // 401 is non-recoverable
        if (apiError.status === 401) {
          const authPayload = {
            code: 'authentication_failed',
            message: apiError.message,
            step,
          };
          onErrorRef.current?.(authPayload);
          notifyParent('error', authPayload);
        }

        throw apiError;
      }
    },
    [apiClient],
  );

  const validateStep = useCallback(
    async (step: StepId, data: Record<string, unknown>): Promise<ValidationResult> => {
      return apiClient.validateStep(step, data);
    },
    [apiClient],
  );

  const completeSession = useCallback(async (): Promise<OnboardSession> => {
    dispatch({ type: 'SUBMIT_START' });
    try {
      const session = await afterLocaleSave(localeSaveRef, () => apiClient.completeSession());
      dispatch({ type: 'SUBMIT_SUCCESS', session });

      if (session.result) {
        const completionPayload = {
          location_id: session.result.location_id,
          tariff_ids: session.result.tariff_ids,
          session_id: session.id,
        };
        onCompleteRef.current?.(completionPayload);
        notifyParent('complete', completionPayload);
      }

      return session;
    } catch (err) {
      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, { code: 'unknown', detail: 'Session completion failed' });
      dispatch({ type: 'SUBMIT_ERROR', error: apiError });
      throw apiError;
    }
  }, [apiClient]);

  const cancelSession = useCallback(
    async (reason?: string): Promise<void> => {
      dispatch({ type: 'SUBMIT_START' });
      try {
        const session = await afterLocaleSave(localeSaveRef, () =>
          apiClient.cancelSession(reason),
        );
        dispatch({ type: 'SUBMIT_SUCCESS', session });
        onCancelRef.current?.();
        notifyParent('cancel');
      } catch (err) {
        const apiError =
          err instanceof ApiError
            ? err
            : new ApiError(0, { code: 'unknown', detail: 'Session cancellation failed' });
        dispatch({ type: 'SUBMIT_ERROR', error: apiError });
        throw apiError;
      }
    },
    [apiClient],
  );

  const goBack = useCallback(async (): Promise<void> => {
    dispatch({ type: 'SUBMIT_START' });
    try {
      const session = await afterLocaleSave(localeSaveRef, () => apiClient.goBack());
      dispatch({ type: 'SUBMIT_SUCCESS', session });

      // Invalidate cached options for steps after the back target — they may
      // become stale if the user changes their selection on the current step.
      if (session.current_step) {
        dispatch({ type: 'INVALIDATE_CACHE_AFTER', step: session.current_step });
      }

      // Fire onStepChange with backward direction
      if (session.current_step) {
        const prevIndex = prevStepRef.current ? getStepIndex(prevStepRef.current) : -1;
        const nextIndex = getStepIndex(session.current_step);
        const direction = nextIndex < prevIndex ? 'backward' : 'forward';

        prevStepRef.current = session.current_step;
        onStepChangeRef.current?.({
          stepId: session.current_step,
          progress: session.progress.percentage,
          direction,
        });
      }
    } catch (err) {
      const apiError =
        err instanceof ApiError
          ? err
          : new ApiError(0, { code: 'unknown', detail: 'Back navigation failed' });

      // already_at_first_step is a no-op — don't surface as error
      if (apiError.code === 'already_at_first_step') {
        const current = stateRef.current.session;
        if (current) dispatch({ type: 'SUBMIT_SUCCESS', session: current });
        return;
      }

      // 401 is non-recoverable
      if (apiError.status === 401) {
        const authPayload = {
          code: 'authentication_failed',
          message: apiError.message,
        };
        onErrorRef.current?.(authPayload);
        notifyParent('error', authPayload);
      }

      dispatch({ type: 'SUBMIT_ERROR', error: apiError });
    }
  }, [apiClient]);

  const navigateToStep = useCallback(
    async (step: StepId): Promise<void> => {
      // Snapshot collected_data BEFORE calling the API so downstream steps
      // can fall back to previously-entered values after re-submission.
      const currentData = stateRef.current.session?.collected_data;
      if (currentData && typeof currentData === 'object') {
        dispatch({ type: 'SNAPSHOT_DATA', data: { ...currentData } });
      }

      dispatch({ type: 'SUBMIT_START' });
      try {
        const session = await afterLocaleSave(localeSaveRef, () =>
          apiClient.navigateToStep(step),
        );
        dispatch({ type: 'SUBMIT_SUCCESS', session });

        // Invalidate cached options for steps after the navigate target
        if (session.current_step) {
          dispatch({ type: 'INVALIDATE_CACHE_AFTER', step: session.current_step });
        }

        // Fire onStepChange with backward direction (navigate always goes back)
        if (session.current_step) {
          prevStepRef.current = session.current_step;
          onStepChangeRef.current?.({
            stepId: session.current_step,
            progress: session.progress.percentage,
            direction: 'backward',
          });
        }
      } catch (err) {
        const apiError =
          err instanceof ApiError
            ? err
            : new ApiError(0, { code: 'unknown', detail: 'Navigate to step failed' });

        // 401 is non-recoverable
        if (apiError.status === 401) {
          const authPayload = {
            code: 'authentication_failed',
            message: apiError.message,
          };
          onErrorRef.current?.(authPayload);
          notifyParent('error', authPayload);
        }

        dispatch({ type: 'SUBMIT_ERROR', error: apiError });
      }
    },
    [apiClient],
  );

  const changeLocale = useCallback(
    async (locale: string): Promise<void> => {
      const target = applyLocale(locale).then((changed) => (changed ? getLocale() : null));
      // Registered before the language file loads, so a step sent meanwhile waits too.
      // Saved in order, without blocking the switch; a failed save only loses it on reload.
      const save = (localeSaveRef.current ?? Promise.resolve())
        .then(() => target)
        .then((active) =>
          active && isSupportedLocale(active) ? apiClient.updateLocale(active) : undefined,
        )
        .then(
          () => undefined,
          () => undefined,
        )
        .finally(() => {
          if (localeSaveRef.current === save) localeSaveRef.current = null;
        });
      localeSaveRef.current = save;
      await target;
    },
    [apiClient, applyLocale],
  );

  // ------- Fetch session on mount -------

  useEffect(() => {
    void fetchSession();
  }, [fetchSession]);

  // When the user changes country, a language the previous country offered
  // (picked from the menu or set by the host) falls back to English if the new
  // country doesn't offer it. A language no country offered is left alone.
  const countryLanguagesRef = useRef<string[]>([]);
  useEffect(() => {
    const session = state.session;
    if (!session?.collected_data['country_code'] || !session.country_languages) return;
    const offers = (codes: string[]) =>
      codes.some((code) => toWidgetLocale(code) === stateRef.current.locale);
    const previous = countryLanguagesRef.current;
    countryLanguagesRef.current = session.country_languages;
    if (offers(previous) && !offers(session.country_languages)) {
      void changeLocale(DEFAULT_LOCALE);
    }
  }, [state.session, changeLocale]);

  // When a session is re-opened at retailer_org or any later step with a
  // retailer already selected, the optionsCache is empty (in-memory only).
  // Pre-fetch retailer_org options so useRetailerLogo / SummaryStep can
  // resolve names and logo immediately.
  //
  // Gated on current_step being at-or-after retailer_org: prevents the
  // effect from re-firing during edit-back navigation (e.g. summary →
  // country) and re-populating the cache with stale-country orgs before
  // the user submits the new country.
  useEffect(() => {
    if (state.isLoading || !state.session) return;
    if (stateRef.current.optionsCache.has('retailer_org')) return;
    const currentStep = state.session.current_step;
    if (!currentStep) return;
    const currentIdx = STEP_ORDER.indexOf(currentStep);
    const retailerOrgIdx = STEP_ORDER.indexOf('retailer_org');
    if (currentIdx === -1 || currentIdx < retailerOrgIdx) return;
    const retailer = state.session.collected_data['retailer'];
    if (typeof retailer !== 'object' || retailer === null) return;
    if (!(retailer as Record<string, unknown>)['organization_id']) return;
    void fetchOptions('retailer_org').catch(() => {});
    // state.locale: a language switch empties the cache, so prefetch again.
  }, [state.isLoading, state.session, state.locale, fetchOptions]);

  // Track step changes from session updates
  useEffect(() => {
    if (state.session?.current_step) {
      prevStepRef.current = state.session.current_step;
    }
  }, [state.session?.current_step]);

  // ------- Memoized context values -------

  const actions = useMemo<SessionActions>(
    () => ({
      fetchSession,
      fetchOptions,
      submitStep,
      validateStep,
      completeSession,
      cancelSession,
      goBack,
      navigateToStep,
      changeLocale,
    }),
    [
      fetchSession,
      fetchOptions,
      submitStep,
      validateStep,
      completeSession,
      cancelSession,
      goBack,
      navigateToStep,
      changeLocale,
    ],
  );

  // Keyed on the locale: a switch remounts every step, so each re-reads
  // `t()` and re-fetches its options in the new language.
  return (
    <StateCtx.Provider value={state}>
      <ActionsCtx.Provider value={actions}>
        <Fragment key={state.locale}>{children}</Fragment>
      </ActionsCtx.Provider>
    </StateCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Access session state. Must be used within a SessionProvider. */
export function useSessionState(): SessionState {
  const ctx = useContext(StateCtx);
  if (!ctx) throw new Error('useSessionState must be used within SessionProvider');
  return ctx;
}

/** Access session actions. Must be used within a SessionProvider. */
export function useSessionActions(): SessionActions {
  const ctx = useContext(ActionsCtx);
  if (!ctx) throw new Error('useSessionActions must be used within SessionProvider');
  return ctx;
}

/** Convenience hook — returns both state and actions. */
export function useSession(): SessionState & SessionActions {
  return { ...useSessionState(), ...useSessionActions() };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStepIndex(step: StepId): number {
  const idx = STEP_ORDER.indexOf(step);
  return idx === -1 ? 0 : idx;
}
